import React from "react";
import { Alert, Divider, Table, Tag, Tooltip } from "antd";
import * as XLSXStyle from "xlsx-js-style";
import {
  CheckCircleOutlined,
  CopyOutlined,
  PauseCircleOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { ModalItem, ButtonItem } from "../../../../../Components/Items";
import { celdaFecha, celdaMoneda, celdaTexto, headerStyle } from "../../../../../Components/ExcelEstilos";
import { colorEstado, etiquetaEstado } from "./analizarFichasPago";

const dinero = (valor) =>
  valor === null || valor === undefined || valor === ""
    ? ""
    : new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(valor) || 0);

const textoArticulos = (articulos) =>
  (articulos || []).map((articulo) => `${articulo.cantidad} x ${articulo.descripcion}`).join(", ");

const COLUMNAS = [
  { titulo: "Fila", ancho: 8, valor: (r) => r.numeroFila },
  { titulo: "Matricula", ancho: 16, valor: (r) => r.matricula },
  { titulo: "Alumno", ancho: 32, valor: (r) => r.alumno },
  { titulo: "Escuela", ancho: 26, valor: (r) => r.escuela },
  { titulo: "Grupo", ancho: 18, valor: (r) => r.grupo },
  { titulo: "Concepto", ancho: 28, valor: (r) => r.concepto },
  // Sólo en las fichas de lista escolar: lo que se le cobró de sus listas.
  { titulo: "Articulos", ancho: 50, valor: (r) => textoArticulos(r.articulos) },
  { titulo: "Periodo", ancho: 14, valor: (r) => r.periodo },
  { titulo: "Resultado", ancho: 22, valor: (r) => etiquetaEstado(r.estado) },
  { titulo: "Folio", ancho: 16, valor: (r) => r.folio },
  { titulo: "Referencia", ancho: 14, valor: (r) => r.referencia },
  { titulo: "Total a pagar", ancho: 16, valor: (r) => r.totalPagar, moneda: true },
  { titulo: "Monto del archivo", ancho: 16, valor: (r) => r.montoExcel, moneda: true },
  { titulo: "Fecha limite", ancho: 14, valor: (r) => r.fechaLimite, fecha: true },
  { titulo: "Correo enviado", ancho: 14, valor: (r) => (r.estado === "creada" && r.correoSolicitado ? (r.correoEnviado ? "Si" : "No") : "") },
  { titulo: "Se envio a", ancho: 34, valor: (r) => r.destinatarios },
  { titulo: "Motivo", ancho: 60, valor: (r) => r.motivo },
  // Cuánto tardó la fila y en qué paso: si la generación se vuelve lenta, aquí se ve dónde.
  { titulo: "Segundos", ancho: 10, valor: (r) => (typeof r.segundos === "number" ? String(r.segundos) : "") },
  { titulo: "Tiempos por paso (s)", ancho: 50, valor: (r) => r.tiempos || "" },
];

const celdaDe = (columna, fila) => {
  const valor = columna.valor(fila);
  if (columna.moneda) return celdaMoneda(valor);
  if (columna.fecha) return celdaFecha(valor);
  return celdaTexto(valor ?? "");
};

const hojaDeResultados = (filas) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    COLUMNAS.map((columna) => ({ v: columna.titulo, t: "s", s: headerStyle })),
    ...filas.map((fila) => COLUMNAS.map((columna) => celdaDe(columna, fila))),
  ]);
  hoja["!cols"] = COLUMNAS.map((columna) => ({ wch: columna.ancho }));
  hoja["!rows"] = [{ hpt: 26 }];
  return hoja;
};

const descargarExcel = (resultados) => {
  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hojaDeResultados(resultados), "Resultados");
  //Siempre se agrega, aunque venga vacía: que exista y esté en blanco dice "no quedó nada
  //pendiente". Una hoja ausente parece un error del reporte.
  XLSXStyle.utils.book_append_sheet(
    libro,
    hojaDeResultados(resultados.filter((valueRes) => valueRes.estado !== "creada")),
    "Sin generar",
  );
  XLSXStyle.writeFile(libro, "resultadosFichasDePago.xlsx");
};

const minutosYSegundos = (segundos) => {
  const total = Math.round(Number(segundos) || 0);
  const minutos = Math.floor(total / 60);
  return minutos > 0 ? `${minutos} min ${total % 60} s` : `${total} s`;
};

const textoDuracion = (duracion) => {
  if (!duracion || !duracion.segundosTotal) return "";
  const lentas = (duracion.etapasMasLentas || []).map((valueEtapa) => `${valueEtapa.etapa} ${valueEtapa.segundos} s`).join(", ");
  return `Tardó ${minutosYSegundos(duracion.segundosTotal)} · ${duracion.segundosPorRenglon} s por fila en promedio${lentas ? ` · lo más lento: ${lentas}` : ""}`;
};

const RESUMEN = [
  { key: "creada", label: "Fichas generadas", icon: <CheckCircleOutlined style={{ color: "#52c41a" }} /> },
  {
    key: "duplicada",
    label: "Ya tenían ficha de ese concepto y periodo (no se duplicaron)",
    icon: <CopyOutlined style={{ color: "#faad14" }} />,
  },
  { key: "error", label: "Con error", icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> },
  {
    key: "detenida",
    label: "No se procesaron (se detuvo la generación)",
    icon: <PauseCircleOutlined style={{ color: "#8c8c8c" }} />,
  },
];

const ResultadosFichasPago = ({ visible, resultado, cerrarModal, descargarPdf, generandoPdf }) => {
  const resultados = resultado?.resultados || [];
  const creadas = resultados.filter((r) => r.estado === "creada");
  const cuentaPor = (estado) => resultados.filter((r) => r.estado === estado).length;
  const sinCorreo = creadas.filter((r) => r.correoSolicitado && !r.correoEnviado);

  const columnas = [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula", width: 120 },
    { title: "Alumno", dataIndex: "alumno", key: "alumno", width: 190 },
    {
      title: "Concepto",
      key: "concepto",
      width: 200,
      render: (_, record) =>
        record.articulos?.length ? (
          <Tooltip title={textoArticulos(record.articulos)}>
            <div>{record.concepto}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>{`${record.articulos.length} artículo(s)`}</div>
          </Tooltip>
        ) : (
          record.concepto
        ),
    },
    { title: "Periodo", dataIndex: "periodo", key: "periodo", width: 100 },
    {
      title: "Resultado",
      key: "estado",
      width: 160,
      render: (_, record) => <Tag color={colorEstado(record.estado)}>{etiquetaEstado(record.estado)}</Tag>,
    },
    { title: "Folio", dataIndex: "folio", key: "folio", width: 120 },
    {
      title: "Referencia",
      dataIndex: "referencia",
      key: "referencia",
      width: 110,
      render: (valor) => (valor ? <b style={{ letterSpacing: 1 }}>{valor}</b> : ""),
    },
    {
      title: "Total",
      key: "totalPagar",
      width: 110,
      align: "right",
      render: (_, record) => dinero(record.totalPagar),
    },
    {
      title: "Correo",
      key: "correoEnviado",
      width: 100,
      align: "center",
      render: (_, record) =>
        record.estado === "creada" && record.correoSolicitado ? (
          <Tag color={record.correoEnviado ? "green" : "orange"}>{record.correoEnviado ? "Enviado" : "No"}</Tag>
        ) : null,
    },
    { title: "Motivo", dataIndex: "motivo", key: "motivo", width: 340 },
    { title: "Seg.", dataIndex: "segundos", key: "segundos", width: 70, align: "right" },
  ];

  return (
    <ModalItem title="Resultado de la generación de fichas" visible={visible} spinning={false} onCancel={cerrarModal} width={1200}>
      {resultado && (
        <>
          {resultado.detenida && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Se detuvo la generación"
              description="Las filas que no alcanzó a procesar salen como «No se procesó (detenida)». Vuelve a subir el mismo archivo: lo que ya quedó sale como «Ya tiene ficha» y sólo se genera lo que faltó."
            />
          )}

          {(resultado.interrumpida || resultado.terminado === false) && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="La generación se interrumpió antes de terminar"
              description={`${resultado.error ? `${resultado.error}. ` : ""}Abajo está lo que sí se procesó (${resultados.length} de ${resultado.total || "?"} filas). Vuelve a subir el mismo archivo: lo que ya quedó no se duplica.`}
            />
          )}

          {textoDuracion(resultado.duracion) && (
            <div style={{ marginBottom: 8, color: "#595959" }}>{textoDuracion(resultado.duracion)}</div>
          )}

          <div>
            {RESUMEN.filter((fila) => cuentaPor(fila.key) > 0).map((fila) => (
              <div key={fila.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                {fila.icon}
                <span style={{ flex: 1, color: "#595959" }}>
                  {fila.key === "duplicada" && resultado.modo === "lista"
                    ? "Ya tenían todos los artículos de su lista (no se duplicaron)"
                    : fila.label}
                </span>
                <span style={{ fontWeight: 500 }}>{cuentaPor(fila.key)}</span>
              </div>
            ))}
          </div>

          {creadas.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message={
                resultado.mostrarEnTienda
                  ? "Las fichas ya están en la cuenta de cada alumno en la tienda en línea"
                  : "Las fichas no se muestran en la tienda en línea"
              }
              description="Para aplicar los pagos usa Ventas eCommerce → Conciliar transferencias por Excel (con el estado de cuenta del banco). También se pueden cobrar desde Cobranza."
            />
          )}

          {sinCorreo.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`${sinCorreo.length} alumnos no recibieron el correo`}
              description="La ficha ya quedó generada; sólo falló el envío (o el alumno no tiene correo). Revisa el motivo en la tabla y entrégales el PDF."
            />
          )}

          <Divider style={{ margin: "12px 0" }} />

          <Table
            size="small"
            rowKey={(record, index) => index}
            dataSource={resultados}
            columns={columnas}
            scroll={{ x: "max-content", y: 360 }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
          />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <ButtonItem onClick={() => descargarExcel(resultados)} disabled={resultados.length === 0}>
              Descargar Resultados
            </ButtonItem>
            <ButtonItem
              icon={<FilePdfOutlined />}
              onClick={descargarPdf}
              disabled={creadas.length === 0}
              loading={generandoPdf}
            >
              Descargar PDF de fichas ({creadas.length})
            </ButtonItem>
            <ButtonItem type="primary" onClick={cerrarModal}>
              Aceptar
            </ButtonItem>
          </div>
        </>
      )}
    </ModalItem>
  );
};

export default ResultadosFichasPago;
